import { useEffect, useState } from "react";
import {
  Button, Card, CardContent, CardHeader, CardTitle, Label, LocaleCombobox,
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem, TimezoneCombobox,
  LoadingState,
} from "@wa/ui";
import { useT, useI18n } from "../../i18n/I18nProvider";
import { api } from "../../lib/api";
import { applyThemeMode, type ThemeMode } from "../../lib/themeMode";
import { useShell } from "../shell/context";

interface Prefs {
  locale: string;
  theme: ThemeMode;
  timezone: string;
  weekStart: number;
}

/** The interface languages the account centre is translated into. */
const UI_LOCALES = ["et", "en"] as const;

export function PreferencesSection() {
  const t = useT();
  const { variant } = useShell();
  const { locale, setLocale } = useI18n();
  const [prefs, setPrefs] = useState<Prefs | null>(null);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    void api.get<Prefs>("/account/preferences").then((p) => {
      setPrefs(p);
      applyThemeMode(p.theme);
      setLocale(p.locale);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function patch(p: Partial<Prefs>) {
    setPrefs((cur) => (cur ? { ...cur, ...p } : cur));
    setSaved(false);
  }

  async function save() {
    if (!prefs) return;
    setBusy(true);
    setSaved(false);
    try {
      const next = await api.put<Prefs>("/account/preferences", prefs);
      setPrefs(next);
      applyThemeMode(next.theme);
      setLocale(next.locale);
      setSaved(true);
      setTimeout(() => setSaved(false), 1500);
    } finally {
      setBusy(false);
    }
  }

  if (!prefs) {
    return (
      <Card>
        <CardContent><LoadingState label={t("common.loading")} /></CardContent>
      </Card>
    );
  }

  return (
    <Card>
      {/* On the portal's account page the section heading above already
          says this word, so the card does not say it twice. */}
      {variant !== "portal" && (
        <CardHeader>
          <CardTitle>{t("preferences.title")}</CardTitle>
        </CardHeader>
      )}
      <CardContent className="space-y-4 max-w-md">
        <div className="space-y-1.5">
          <Label htmlFor="pref-locale">{t("preferences.language")}</Label>
          <LocaleCombobox
            id="pref-locale"
            locales={UI_LOCALES}
            displayLocale={locale}
            value={prefs.locale}
            onChange={(v) => patch({ locale: v })}
            searchPlaceholder={t("common.localeSearch")}
            emptyLabel={t("common.localeNone")}
          />
        </div>

        <div className="space-y-1.5">
          <Label>{t("preferences.theme")}</Label>
          <Select value={prefs.theme} onValueChange={(v) => { patch({ theme: v as ThemeMode }); applyThemeMode(v as ThemeMode); }}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="system">{t("preferences.themeSystem")}</SelectItem>
              <SelectItem value="light">{t("preferences.themeLight")}</SelectItem>
              <SelectItem value="dark">{t("preferences.themeDark")}</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="pref-timezone">{t("preferences.timezone")}</Label>
          <TimezoneCombobox
            id="pref-timezone"
            value={prefs.timezone}
            onChange={(v) => patch({ timezone: v })}
            searchPlaceholder={t("common.timezoneSearch")}
            emptyLabel={t("common.timezoneNone")}
          />
        </div>

        <Button onClick={save} loading={busy}>
          {saved ? t("common.saved") : t("preferences.save")}
        </Button>
      </CardContent>
    </Card>
  );
}
